import React, { useState, useEffect, useRef } from "react";
import "../css/Common.css";
import "./css/SubscriptionPayment.css";
import "./css/Subscriptions.css";
import "../css/Accordion.css";
import AccountAPI from "../services/AccountAPI";
import { getCookie } from "../utils/CookieUtil";
import { useUser } from "../context/UserContext";
import { useSubscription } from "../context/SubscriptionContext";

import { formatDateOnly } from "../utils/DateTimeUtil";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUser,
  faUsers,
  faCalendarAlt,
  faCheckCircle,
  faTimesCircle,
  faChevronRight,
  faCreditCard,
  faReceipt,
  faShieldAlt,
  faExclamationTriangle,
  faArrowDown,
  faGift,
} from "@fortawesome/free-solid-svg-icons";
import RefreshButton from "../common/RefreshButton";
import LoadingSpinner from "../common/LoadingSpinner";

// Subscription imports
import ServicePlans from "../plans/ServicePlans";
import ConfirmationModal from "../common/ConfirmationModal";
import SelectedPlanPreview from "./components/SelectedPlanPreview";
import { useSubscriptionData } from "./hooks/useSubscriptionData";
import { useSubscriptionActions } from "./hooks/useSubscriptionActions";
import { formatDate } from "./utils/subscriptionHelpers";
import { calculateNextPaymentDate } from "../utils/SubscriptionUtil";

// Payment imports
import PaymentMethods from "../payments/PaymentMethods";
import PaymentHistory from "../payments/PaymentHistory";
import SubscriptionsAPI from "../services/SubscriptionsAPI";
import { useTranslation } from "../../../i18n/LanguageContext";

// ─── Helpers ───────────────────────────────────────────────

const capitalizeFirst = (str) => {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

const formatPlanName = (planName) => {
  if (!planName) return null;
  const lowerName = planName.toLowerCase();
  if (lowerName === "family") return "Family Plan";
  if (lowerName === "extended_family") return "Extended Family Plan";
  if (lowerName === "individual") return "Individual Plan";
  if (lowerName === "free") return "Free Plan";
  if (lowerName === "premium") return "Premium Plan";
  return planName;
};

// ─── Component ─────────────────────────────────────────────

function SubscriptionPayment({
  setHeroContent,
  initialSection,
  autoOpenAddCard,
}) {
  const { logoutUser, isDemoSeat } = useUser();
  const {
    getMaxUsers,
    getDiscountedPrice,
    priceInfo,
    hasActiveSubscription,
    hasLapsedSubscription,
  } = useSubscription();
  const { t } = useTranslation();

  // ── Account state ──
  const [accountData, setAccountData] = useState(null);
  const [accountLoading, setAccountLoading] = useState(true);
  const [accountError, setAccountError] = useState(null);

  // ── Section expand/collapse (exclusive accordion) ──
  const [expandedSection, setExpandedSection] = useState(
    initialSection || null,
  );
  const [showPlanDetails, setShowPlanDetails] = useState(false);

  // ── Trial conversion promo code state ──
  const [convertPromoCode, setConvertPromoCode] = useState("");
  const [convertPromoValidating, setConvertPromoValidating] = useState(false);
  const [convertPromoResult, setConvertPromoResult] = useState(null);
  const [showConvertPromoInput, setShowConvertPromoInput] = useState(false);

  // ── Scheduled promo code state (active paid subs — takes effect next cycle) ──
  const [subPromoCode, setSubPromoCode] = useState("");
  const [subPromoValidating, setSubPromoValidating] = useState(false);
  const [subPromoResult, setSubPromoResult] = useState(null);
  const [showSubPromoInput, setShowSubPromoInput] = useState(false);

  // Auto-expand and scroll to section when navigated with initialSection
  useEffect(() => {
    if (initialSection) {
      setExpandedSection(initialSection);
      // Scroll to the section after a brief delay for render
      setTimeout(() => {
        const el = document.getElementById(`ab-section-${initialSection}`);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 300);
    }
  }, [initialSection]);

  const toggleSection = (sectionId) => {
    setExpandedSection((prev) => (prev === sectionId ? null : sectionId));
  };

  // Accordion section renderer (same pattern as Settings page)
  const renderAccordion = ({
    id,
    icon,
    iconClass,
    title,
    subtitle,
    statusBadge,
    trialBadge,
    secondaryBadge,
    expanded,
    onToggle,
    children,
  }) => (
    <div className="settings-accordion" id={`ab-section-${id}`}>
      <div className="settings-accordion-header" onClick={onToggle}>
        <div className={`accordion-icon ${iconClass}`}>
          <FontAwesomeIcon icon={icon} />
        </div>
        <div className="accordion-title-group">
          <h3 className="accordion-title">{title}</h3>
          <p className="accordion-subtitle">{subtitle}</p>
        </div>
        {statusBadge && (
          <span
            className={`accordion-status-badge ${statusBadge.className || ""}`}
          >
            {statusBadge.icon && <FontAwesomeIcon icon={statusBadge.icon} />}
            {statusBadge.label}
          </span>
        )}
        {trialBadge && (
          <span
            className={`accordion-status-badge ${trialBadge.className || ""}`}
            title={trialBadge.title}
          >
            {trialBadge.icon && <FontAwesomeIcon icon={trialBadge.icon} />}
            {trialBadge.label}
          </span>
        )}
        {secondaryBadge && (
          <span
            className={`accordion-status-badge ${secondaryBadge.className || ""}`}
            title={secondaryBadge.title}
          >
            {secondaryBadge.icon && (
              <FontAwesomeIcon icon={secondaryBadge.icon} />
            )}
            {secondaryBadge.label}
          </span>
        )}
        <FontAwesomeIcon
          icon={faChevronRight}
          className={`accordion-chevron ${expanded ? "expanded" : ""}`}
        />
      </div>
      <div
        className={`settings-accordion-content ${expanded ? "expanded" : ""}`}
        style={expanded ? undefined : { display: "none" }}
      >
        {children}
      </div>
    </div>
  );

  // ── Refs ──
  const errorRef = useRef(null);
  const selectedPlanRef = useRef(null);

  // ── Force-open the "Add New Card" form when payment is required ──
  const [forceOpenAddCard, setForceOpenAddCard] = useState(false);

  // ── Subscription hooks ──
  const {
    currentSubscription,
    loading: subLoading,
    error: subError,
    currentServicePlan,
    fetchSubscriptionData,
  } = useSubscriptionData();

  const {
    showServicePlans,
    selectedPlan,
    subscribing,
    subscriptionError,
    showPlanChangeConfirm,
    showCancelConfirm,
    planChangeDetails,
    setSubscriptionError,
    handleViewPlans,
    handleCloseServicePlans,
    handlePlanSelectedFromModal,
    handleSubscribeToPlan,
    handleClearSelectedPlan,
    handleCancelSubscription,
    handleCancelDowngrade,
    handleUndoCancelSubscription,
    handleApplyPromo,
    handleCancelPendingPromo,
    confirmPlanChange,
    cancelPlanChange,
    executeCancelSubscription,
    cancelSubscriptionConfirm,
    showConvertTrialConfirm,
    handleConvertTrialToPaid,
    executeConvertTrialToPaid,
    cancelConvertTrialConfirm,
  } = useSubscriptionActions(fetchSubscriptionData, currentServicePlan, () => {
    setForceOpenAddCard(true);
    setExpandedSection("payment");
    setTimeout(() => {
      const el = document.getElementById("ab-section-payment");
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 150);
  });

  // ── Scroll to error ──
  useEffect(() => {
    if (subscriptionError && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [subscriptionError]);

  // ── Scroll to selected plan preview when a plan is chosen ──
  useEffect(() => {
    if (selectedPlan) {
      // Only auto-switch to subscription section if user isn't actively
      // entering payment info — switching away would disrupt their input
      if (expandedSection !== "payment") {
        setExpandedSection("subscription");
      }
      // Scroll after a brief delay so the accordion expands and preview renders
      setTimeout(() => {
        if (selectedPlanRef.current) {
          selectedPlanRef.current.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
      }, 350);
    }
  }, [selectedPlan]);

  // ── Account data fetch ──
  useEffect(() => {
    fetchAccountData();
  }, []);

  const fetchAccountData = async () => {
    try {
      setAccountLoading(true);
      setAccountError(null);

      const accountOwnerUuid = getCookie("uuid");
      if (!accountOwnerUuid) {
        // Mount-time race guard: see Account.js for the full rationale.
        // Calling logoutUser() here clobbers a freshly-issued session.
        console.log(
          "SubscriptionPayment: No UUID cookie yet — deferring; auth gate will handle",
        );
        setAccountLoading(false);
        return;
      }

      const response = await AccountAPI.getAccountByUserUuid();
      const accountInfo = response.data || response;

      if (!accountInfo) {
        setAccountError(t("ab.error.unableToLoad"));
        return;
      }
      setAccountData(accountInfo);
    } catch (err) {
      console.error("Error fetching account data:", err);
      setAccountError(t("ab.error.failedToLoad"));
    } finally {
      setAccountLoading(false);
    }
  };

  const resetConvertPromoState = () => {
    setConvertPromoCode("");
    setConvertPromoValidating(false);
    setConvertPromoResult(null);
    setShowConvertPromoInput(false);
  };

  const handleValidateConvertPromoCode = async () => {
    if (!convertPromoCode.trim()) return;
    setConvertPromoValidating(true);
    try {
      const response = await SubscriptionsAPI.validatePromoCode(
        convertPromoCode.trim(),
        currentSubscription?.planId,
        currentSubscription?.paymentCycle || "MONTHLY",
      );
      setConvertPromoResult(response);
    } catch {
      setConvertPromoResult({
        valid: false,
        message: t("ab.promo.validateError"),
      });
    } finally {
      setConvertPromoValidating(false);
    }
  };

  const resetSubPromoState = () => {
    setSubPromoCode("");
    setSubPromoValidating(false);
    setSubPromoResult(null);
    setShowSubPromoInput(false);
  };

  // Validate a promo code for an active paid subscription. The discount takes
  // effect at the NEXT billing date, so we validate against whatever plan/cycle
  // will be in effect next period (pending plan/cycle if set, else current).
  const handleValidateSubPromoCode = async () => {
    if (!subPromoCode.trim()) return;
    setSubPromoValidating(true);
    try {
      const effectivePlanId =
        currentSubscription?.pendingPlanId ?? currentSubscription?.planId;
      const effectiveCycle =
        currentSubscription?.pendingPaymentCycle ||
        currentSubscription?.paymentCycle ||
        "MONTHLY";
      const response = await SubscriptionsAPI.validatePromoCode(
        subPromoCode.trim(),
        effectivePlanId,
        effectiveCycle,
      );
      setSubPromoResult(response);
    } catch {
      setSubPromoResult({
        valid: false,
        message: t("ab.promo.validateError"),
      });
    } finally {
      setSubPromoValidating(false);
    }
  };

  // Schedule the validated promo code so it applies at the next billing date.
  const handleScheduleSubPromo = async () => {
    if (!subPromoCode.trim()) return;
    const ok = await handleApplyPromo(subPromoCode.trim());
    if (ok) {
      resetSubPromoState();
    }
  };

  const handleRefresh = () => {
    fetchAccountData();
    fetchSubscriptionData();
  };

  // H-1: Open the "Add New Card" flow so the user can fix a failed renewal
  // payment without leaving the page (expands the payment section and forces
  // the add-card form open, mirroring the trial-conversion payment prompt).
  const openUpdatePaymentMethod = () => {
    setForceOpenAddCard(true);
    setExpandedSection("payment");
    setTimeout(() => {
      const el = document.getElementById("ab-section-payment");
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 150);
  };

  // ── Subscription helpers ──
  const getNextPaymentInfo = () => {
    if (!currentSubscription) return null;

    // Check if subscription is pending cancellation (active but cancelled)
    const isPendingCancel =
      currentSubscription.status?.toLowerCase() === "active" &&
      currentSubscription.cancelledAt != null;

    // Check if subscription is fully cancelled or expired
    const statusLower = currentSubscription.status?.toLowerCase();
    const isCancelledOrExpired =
      statusLower === "cancelled" || statusLower === "expired";
    const isGracePeriod = statusLower === "grace_period";

    if (isGracePeriod) {
      return {
        label: t("ab.renewal.label.gracePeriodEnds"),
        date: formatDate(currentSubscription.graceEndDate),
        sublabel: t("ab.renewal.sublabel.addPayment"),
      };
    }

    if (currentSubscription.isTrial) {
      const trialEndDate =
        currentSubscription.trialEndDate || currentSubscription.endDate;

      if (isPendingCancel) {
        return {
          label: t("ab.renewal.label.accessUntil"),
          date: formatDate(trialEndDate),
          sublabel: t("ab.renewal.sublabel.willNotRenew"),
        };
      }

      const autoRenewDate = currentSubscription.autoRenew
        ? calculateNextPaymentDate(
            currentSubscription.startDate,
            currentSubscription.paymentCycle,
            currentSubscription,
          )
        : null;

      return {
        label: t("ab.renewal.label.trialEnds"),
        date: formatDate(trialEndDate),
        sublabel: autoRenewDate
          ? t("ab.renewal.sublabel.autoRenews").replace(
              "{date}",
              formatDate(autoRenewDate),
            )
          : null,
      };
    }

    if (isPendingCancel) {
      const nextPaymentDate = calculateNextPaymentDate(
        currentSubscription.startDate,
        currentSubscription.paymentCycle,
        currentSubscription,
      );
      return {
        label: t("ab.renewal.label.accessUntil"),
        date: nextPaymentDate
          ? formatDate(nextPaymentDate)
          : t("ab.renewal.date.endOfBillingPeriod"),
        sublabel: t("ab.renewal.sublabel.cancellationScheduled"),
      };
    }

    // Cancelled/expired subscriptions: show access-until, never show renewal or downgrade info.
    // IMPORTANT: prefer endDate (frozen at cancel time by the server) over nextBillingDate.
    // nextBillingDate can drift forward via Stripe customer.subscription.updated webhooks
    // even on cancelled rows; endDate is the authoritative access cutoff.
    if (isCancelledOrExpired) {
      const accessUntilDate =
        currentSubscription.endDate ||
        currentSubscription.nextBillingDate ||
        calculateNextPaymentDate(
          currentSubscription.startDate,
          currentSubscription.paymentCycle,
          currentSubscription,
        );
      return {
        label: t("ab.renewal.label.accessUntil"),
        date: accessUntilDate
          ? formatDate(accessUntilDate)
          : t("ab.renewal.date.endOfBillingPeriod"),
        sublabel: t("ab.renewal.sublabel.subscriptionCancelled"),
      };
    }

    const nextPaymentDate = calculateNextPaymentDate(
      currentSubscription.startDate,
      currentSubscription.paymentCycle,
      currentSubscription,
    );

    // If a downgrade is pending, enrich the renewal sublabel
    if (currentSubscription.pendingPlanId != null) {
      // Skip stale downgrade info if the change date has already passed
      const changeDate = currentSubscription.pendingPlanChangeDate
        ? new Date(currentSubscription.pendingPlanChangeDate)
        : null;
      const isStale = changeDate && changeDate < new Date();

      if (!isStale) {
        const pendingName =
          formatPlanName(currentSubscription.pendingPlanDisplayName) ||
          t("ab.downgrade.fallbackPlan");
        const pendingPrice = currentSubscription.pendingPlanPrice;
        const cycle =
          currentSubscription.pendingPaymentCycle ||
          currentSubscription.paymentCycle ||
          "month";
        const cycleLabel = cycle === "yearly" ? "yr" : "mo";
        const discPct = currentSubscription.discountPercent || 0;
        let priceLabel = "";
        if (pendingPrice) {
          const base = Number(pendingPrice);
          if (discPct > 0) {
            const discounted = (base * (1 - discPct / 100)).toFixed(2);
            priceLabel = ` ($${discounted}/${cycleLabel})`;
          } else {
            priceLabel = ` ($${base.toFixed(2)}/${cycleLabel})`;
          }
        }
        return {
          label: t("ab.renewal.label.nextRenewal"),
          date: nextPaymentDate ? formatDate(nextPaymentDate) : "N/A",
          sublabel: t("ab.renewal.sublabel.planSwitchesTo")
            .replace("{name}", pendingName)
            .replace("{price}", priceLabel),
        };
      }
    }

    return {
      label: t("ab.renewal.label.nextRenewal"),
      date: nextPaymentDate ? formatDate(nextPaymentDate) : "N/A",
      sublabel: null,
    };
  };

  const paymentInfo = getNextPaymentInfo();

  // A promo code saved on an active trial is stored in the regular promo fields
  // and auto-applies when the trial converts to paid. Surface it in the trial
  // notice and the convert-trial modal so the user sees the locked-in discount.
  const savedTrialPromoCode =
    currentSubscription?.isTrial && currentSubscription?.promoCode
      ? currentSubscription.promoCode
      : null;
  const savedTrialPromoPct = savedTrialPromoCode
    ? Number(currentSubscription?.discountPercent) || 0
    : 0;

  // During a trial the promo hasn't been applied to subscription.amount yet,
  // so priceInfo.displayPrice still shows the full price. Compute the real
  // post-discount amount so the card doesn't confusingly show e.g. "100% off
  // $14.99/month" where both values are $14.99.
  const trialPromoDisplayPrice =
    currentSubscription?.isTrial && savedTrialPromoPct > 0
      ? priceInfo.basePrice * (1 - savedTrialPromoPct / 100)
      : null;

  const getAccessMessage = () => {
    if (!currentSubscription) return null;

    const statusLower = currentSubscription.status?.toLowerCase();
    const isPendingCancel =
      statusLower === "active" && currentSubscription.cancelledAt != null;
    const isCancelledOrExpired =
      statusLower === "cancelled" || statusLower === "expired";

    if (!isPendingCancel && !isCancelledOrExpired) return null;

    if (currentSubscription.isTrial) {
      return t("ab.access.untilEndOfTrial");
    }

    // For fully cancelled/expired subs, endDate is frozen at cancel time and is
    // the authoritative access cutoff. nextBillingDate can drift via webhooks
    // and must not be used here. Pending-cancel (status=active) hasn't yet had
    // endDate written, so fall back to nextBillingDate then to a computed date.
    const endDate =
      (isCancelledOrExpired ? currentSubscription.endDate : null) ||
      currentSubscription.nextBillingDate ||
      currentSubscription.endDate ||
      calculateNextPaymentDate(
        currentSubscription.startDate,
        currentSubscription.paymentCycle,
        currentSubscription,
      );
    const formattedEnd = endDate
      ? formatDate(endDate)
      : t("ab.renewal.date.endOfBillingPeriod");
    return t("ab.access.until").replace("{date}", formattedEnd);
  };

  const accessMessage = getAccessMessage();

  const renderFeature = (label, value, isBoolean = false) => {
    if (isBoolean) {
      return (
        <div className="subscription-feature-item">
          <FontAwesomeIcon
            icon={value ? faCheckCircle : faTimesCircle}
            className={value ? "feature-included" : "feature-not-included"}
          />
          <span>{label}</span>
        </div>
      );
    }
    return (
      <div className="subscription-feature-item subscription-feature-limit">
        <span className="feature-value">{value}</span>
        <span className="feature-label">{label}</span>
      </div>
    );
  };

  // ── Loading state ──
  if (accountLoading && subLoading) {
    return (
      <div className="common-container">
        <LoadingSpinner message={t("ab.loading")} />
      </div>
    );
  }

  // ── Full page error ──
  if (accountError && subError) {
    return (
      <div className="common-container">
        <div className="ab-error">
          <h2 className="main-title">{t("ab.title")}</h2>
          <div className="error-message">
            <p>{accountError}</p>
            <button onClick={handleRefresh} className="btn btn-primary">
              {t("common.retry")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════
  //  RENDER
  // ═══════════════════════════════════════════════════════════

  return (
    // <div className="account-billing-page">
    <div className="common-container">
      {/* ── Page Header ── */}
      <div className="common-header ab-header">
        <div className="ab-header-left">
          <h1 className="main-title">{t("ab.title")}</h1>
          <p className="common-subtitle">{t("ab.subtitle")}</p>
        </div>
        <RefreshButton onClick={handleRefresh} title={t("ab.refreshTitle")} />
      </div>

      <div className="subscription-payment-page">
        {/* ═══════════════════════════════════════════════════ */}
        {/*  SECTION 1 — Account Overview (stat strip)        */}
        {/* ═══════════════════════════════════════════════════ */}
        <div className="ab-stats-strip">
          <div className="ab-stat-card">
            <div className="ab-stat-icon">
              <FontAwesomeIcon icon={faUser} />
            </div>
            <div className="ab-stat-info">
              <span className="ab-stat-label">{t("ab.stat.account")}</span>
              <span className="ab-stat-value">
                {subLoading
                  ? t("common.loading")
                  : formatPlanName(
                      currentServicePlan?.displayName ||
                        currentSubscription?.planName,
                    ) ||
                    t(
                      hasActiveSubscription
                        ? "ab.stat.myAccount"
                        : "ab.stat.noPlan",
                    )}
              </span>
            </div>
          </div>

          <div className="ab-stat-card">
            <div className="ab-stat-icon status">
              <FontAwesomeIcon icon={faShieldAlt} />
            </div>
            <div className="ab-stat-info">
              <span className="ab-stat-label">{t("ab.stat.status")}</span>
              <span className="ab-stat-value">
                {subLoading ? (
                  t("common.loading")
                ) : (
                  <span
                    className={`ab-status-badge ${
                      hasActiveSubscription ? "active" : "inactive"
                    }`}
                  >
                    {hasActiveSubscription
                      ? t("ab.status.active")
                      : hasLapsedSubscription
                        ? t("ab.status.lapsed")
                        : t("ab.status.noSub")}
                  </span>
                )}
              </span>
            </div>
          </div>

          <div className="ab-stat-card">
            <div className="ab-stat-icon date">
              <FontAwesomeIcon icon={faCalendarAlt} />
            </div>
            <div className="ab-stat-info">
              <span className="ab-stat-label">{t("ab.stat.memberSince")}</span>
              <span className="ab-stat-value">
                {subLoading ? "..." : paymentInfo?.date || "N/A"}
              </span>
            </div>
          </div>

          <div className="ab-stat-card">
            <div className="ab-stat-icon users">
              <FontAwesomeIcon icon={faUsers} />
            </div>
            <div className="ab-stat-info">
              <span className="ab-stat-label">{t("ab.stat.users")}</span>
              <span className="ab-stat-value">
                {accountLoading
                  ? "..."
                  : (() => {
                      const count = accountData?.userCount || 0;
                      // No active plan (lapsed or never subscribed): the seat
                      // limit is not meaningful, so show only the current
                      // member count instead of a nonsensical "X of 1".
                      if (!hasActiveSubscription) return `${count}`;
                      const max = getMaxUsers();
                      if (max === -1)
                        return t("ab.stat.usersUnlimited").replace(
                          "{count}",
                          count,
                        );
                      if (!max) return `${count}`;
                      return `${count} of ${max}`;
                    })()}
              </span>
              <button
                className="ab-stat-action-inline"
                onClick={(e) => {
                  e.stopPropagation();
                  setHeroContent("Member User Info");
                }}
                title={t("ab.stat.manageTitle")}
              >
                {t("ab.stat.manageBtn")}
              </button>
            </div>
          </div>
        </div>
        {/* ═══════════════════════════════════════════════════ */}
        {/*  SECTION 2 — Subscription                         */}
        {/* ═══════════════════════════════════════════════════ */}
        {renderAccordion({
          id: "subscription",
          icon: faShieldAlt,
          iconClass: "subscription",
          title: t("ab.section.subscription.title"),
          subtitle: t("ab.section.subscription.subtitle"),
          trialBadge: currentSubscription?.isTrial
            ? {
                icon: faGift,
                label: t("ab.badge.freeTrial"),
                className: "badge-trial",
                title: t("ab.badge.freeTrialTitle"),
              }
            : null,
          statusBadge: currentSubscription
            ? (() => {
                const isPendingCancel =
                  currentSubscription.status?.toLowerCase() === "active" &&
                  currentSubscription.cancelledAt != null;
                const isGrace =
                  currentSubscription.status?.toLowerCase() === "grace_period";
                return {
                  label: isPendingCancel
                    ? t("ab.status.cancelling")
                    : isGrace
                      ? t("ab.status.gracePeriod")
                      : (() => {
                          const s = currentSubscription.status?.toLowerCase();
                          if (s === "active") return t("ab.status.active");
                          if (s === "inactive") return t("ab.status.inactive");
                          if (s === "cancelled" || s === "canceled")
                            return t("ab.status.cancelled");
                          if (s === "expired") return t("ab.status.expired");
                          return (
                            currentSubscription.status || t("ab.status.active")
                          );
                        })(),
                  className: isPendingCancel
                    ? "badge-warning"
                    : isGrace
                      ? "badge-danger"
                      : `badge-${currentSubscription.status?.toLowerCase() === "active" ? "success" : "warning"}`,
                  icon:
                    isPendingCancel || isGrace
                      ? faExclamationTriangle
                      : undefined,
                };
              })()
            : null,
          secondaryBadge:
            currentSubscription?.pendingPlanId != null &&
            !currentSubscription.cancelledAt &&
            currentSubscription.status?.toLowerCase() !== "cancelled" &&
            currentSubscription.status?.toLowerCase() !== "expired" &&
            (() => {
              const cd = currentSubscription.pendingPlanChangeDate
                ? new Date(currentSubscription.pendingPlanChangeDate)
                : null;
              return !cd || cd >= new Date();
            })()
              ? {
                  icon: faArrowDown,
                  label: "",
                  className: "badge-downgrade",
                  title: t("ab.badge.downgradePendingTitle"),
                }
              : null,
          expanded: expandedSection === "subscription",
          onToggle: () => toggleSection("subscription"),
          children: subLoading ? (
            <LoadingSpinner
              message={t("ab.loading.subscription")}
              size="sm"
              className="compact"
            />
          ) : subError ? (
            <div className="ab-section-error">
              <p>{subError}</p>
              <button onClick={fetchSubscriptionData} className="btn btn-ghost">
                {t("common.retry")}
              </button>
            </div>
          ) : (
            <div className="subscription-card">
              {subscribing ? (
                <LoadingSpinner message={t("ab.loading.processing")} />
              ) : currentSubscription ? (
                <>
                  {/* Subscription Summary */}
                  <div className="subscription-card-header">
                    <div className="subscription-card-info">
                      <h2 className="subscription-plan-title">
                        {formatPlanName(
                          currentServicePlan?.displayName ||
                            currentSubscription.planName,
                        ) || t("ab.plan.currentPlan")}
                      </h2>
                      <div className="subscription-meta">
                        {(() => {
                          const isPendingCancel =
                            currentSubscription.status?.toLowerCase() ===
                              "active" &&
                            currentSubscription.cancelledAt != null;

                          if (isPendingCancel) {
                            const endDate = currentSubscription.isTrial
                              ? new Date(
                                  currentSubscription.trialEndDate ||
                                    currentSubscription.endDate,
                                )
                              : calculateNextPaymentDate(
                                  currentSubscription.startDate,
                                  currentSubscription.paymentCycle,
                                  currentSubscription,
                                );
                            const daysLeft = endDate
                              ? Math.max(
                                  0,
                                  Math.ceil(
                                    (endDate - new Date()) /
                                      (1000 * 60 * 60 * 24),
                                  ),
                                )
                              : null;

                            return (
                              <span className="subscription-status-badge cancelling">
                                {t("ab.status.cancelling")}
                                {daysLeft != null
                                  ? ` \u00b7 ${t("ab.daysLeft").replace("{n}", daysLeft)}`
                                  : ""}
                              </span>
                            );
                          }

                          if (
                            currentSubscription.status?.toLowerCase() ===
                            "grace_period"
                          ) {
                            const graceEnd = currentSubscription.graceEndDate
                              ? new Date(currentSubscription.graceEndDate)
                              : null;
                            const daysLeft = graceEnd
                              ? Math.max(
                                  0,
                                  Math.ceil(
                                    (graceEnd - new Date()) /
                                      (1000 * 60 * 60 * 24),
                                  ),
                                )
                              : null;
                            return (
                              <span className="subscription-status-badge grace-period">
                                {t("ab.status.gracePeriod")}
                                {daysLeft != null
                                  ? ` · ${t("ab.daysLeft").replace("{n}", daysLeft)}`
                                  : ""}
                              </span>
                            );
                          }

                          return (
                            <span
                              className={`subscription-status-badge ${currentSubscription.status?.toLowerCase()}`}
                            >
                              {(() => {
                                const s =
                                  currentSubscription.status?.toLowerCase();
                                if (s === "active")
                                  return t("ab.status.active");
                                if (s === "inactive")
                                  return t("ab.status.inactive");
                                if (s === "cancelled" || s === "canceled")
                                  return t("ab.status.cancelled");
                                if (s === "expired")
                                  return t("ab.status.expired");
                                return (
                                  currentSubscription.status ||
                                  t("ab.status.active")
                                );
                              })()}
                            </span>
                          );
                        })()}
                        {currentSubscription.pendingPlanId != null &&
                          !currentSubscription.cancelledAt &&
                          currentSubscription.status?.toLowerCase() !==
                            "cancelled" &&
                          currentSubscription.status?.toLowerCase() !==
                            "expired" &&
                          (() => {
                            const cd = currentSubscription.pendingPlanChangeDate
                              ? new Date(
                                  currentSubscription.pendingPlanChangeDate,
                                )
                              : null;
                            return !cd || cd >= new Date();
                          })() && (
                            <span className="subscription-status-badge downgrade-pending">
                              {t("ab.status.downgradePending")}
                            </span>
                          )}
                        <span className="subscription-cycle">
                          {(() => {
                            const c = (
                              currentSubscription.paymentCycle || "monthly"
                            ).toLowerCase();
                            if (c === "monthly") return t("ab.cycle.monthly");
                            if (c === "yearly" || c === "annual")
                              return t("ab.cycle.yearly");
                            return capitalizeFirst(
                              currentSubscription.paymentCycle || "Monthly",
                            );
                          })()}
                          {currentSubscription.autoRenew
                            ? ` \u2022 ${t("ab.cycle.autoRenew")}`
                            : ""}
                        </span>
                      </div>
                    </div>
                    <div className="subscription-card-price">
                      {(priceInfo.hasDiscount ||
                        trialPromoDisplayPrice !== null) && (
                        <div className="price-discount-row">
                          <span className="price-original-struck">
                            ${priceInfo.basePrice.toFixed(2)}
                          </span>
                          <span className="price-discount-badge">
                            {t("ab.price.off").replace(
                              "{pct}",
                              priceInfo.discountPercent,
                            )}
                          </span>
                        </div>
                      )}
                      <span className="price-amount">
                        $
                        {(
                          trialPromoDisplayPrice ?? priceInfo.displayPrice
                        ).toFixed(2)}
                      </span>
                      <span className="price-period">
                        /
                        {priceInfo.period === "year"
                          ? t("ab.period.year")
                          : t("ab.period.month")}
                      </span>
                    </div>
                  </div>

                  {/* H-1: Billing-issue / failed-payment recovery banner.
                      Shown when a renewal payment has failed (PAST_DUE /
                      GRACE_PERIOD, or a lingering lastPaymentFailedAt). Offers
                      a direct "Pay now" link to the Stripe hosted invoice and
                      an "Update payment method" action. */}
                  {(() => {
                    const statusLower =
                      currentSubscription.status?.toLowerCase();
                    const hasBillingIssue =
                      statusLower === "past_due" ||
                      statusLower === "grace_period" ||
                      (currentSubscription.lastPaymentFailedAt != null &&
                        statusLower !== "active" &&
                        statusLower !== "cancelled" &&
                        statusLower !== "expired");
                    if (!hasBillingIssue) return null;

                    const nextAttempt =
                      currentSubscription.nextPaymentAttemptDate;
                    return (
                      <div className="subscription-billing-issue-banner">
                        <span className="billing-issue-icon">⚠️</span>
                        <div className="billing-issue-details">
                          <strong>{t("ab.billing.title")}</strong>
                          <span>
                            {currentSubscription.lastPaymentFailedAt
                              ? t("ab.billing.failedOn").replace(
                                  "{date}",
                                  formatDate(
                                    currentSubscription.lastPaymentFailedAt,
                                  ),
                                )
                              : t("ab.billing.failedNoDate")}{" "}
                            {nextAttempt
                              ? t("ab.billing.retryOn").replace(
                                  "{date}",
                                  formatDate(nextAttempt),
                                ) + " "
                              : ""}
                            {t("ab.billing.updateOrPayNow")}
                          </span>
                        </div>
                        <div className="billing-issue-actions">
                          {currentSubscription.hostedInvoiceUrl && (
                            <a
                              className="billing-issue-pay-btn"
                              href={currentSubscription.hostedInvoiceUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {t("ab.billing.payNow")}
                            </a>
                          )}
                          <button
                            className="billing-issue-update-btn"
                            type="button"
                            onClick={openUpdatePaymentMethod}
                          >
                            {t("ab.billing.updatePaymentMethod")}
                          </button>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Renewal Info */}
                  {paymentInfo && (
                    <div className="subscription-renewal-info">
                      <span className="renewal-label">
                        {paymentInfo.label}:
                      </span>
                      <span className="renewal-date">{paymentInfo.date}</span>
                      {paymentInfo.sublabel && (
                        <span className="renewal-sublabel">
                          {paymentInfo.sublabel}
                        </span>
                      )}
                      {accessMessage && (
                        <span className="renewal-sublabel">
                          <strong>{accessMessage}</strong>
                        </span>
                      )}
                    </div>
                  )}

                  {/* Pending Downgrade Notice — hidden when cancelled/expired or past due date */}
                  {currentSubscription.pendingPlanId != null &&
                    !currentSubscription.cancelledAt &&
                    currentSubscription.status?.toLowerCase() !== "cancelled" &&
                    currentSubscription.status?.toLowerCase() !== "expired" &&
                    (() => {
                      // Hide if the scheduled downgrade date has already passed
                      const changeDate =
                        currentSubscription.pendingPlanChangeDate
                          ? new Date(currentSubscription.pendingPlanChangeDate)
                          : null;
                      if (changeDate && changeDate < new Date()) return null;
                      return true;
                    })() &&
                    (() => {
                      const pendingName =
                        formatPlanName(
                          currentSubscription.pendingPlanDisplayName,
                        ) || t("ab.downgrade.fallbackPlan");
                      const pendingPrice = currentSubscription.pendingPlanPrice;
                      const cycle =
                        currentSubscription.pendingPaymentCycle ||
                        currentSubscription.paymentCycle ||
                        "month";
                      const cycleLabel = cycle === "yearly" ? "yr" : "mo";
                      const discPct = currentSubscription.discountPercent || 0;
                      let priceLabel = "";
                      if (pendingPrice) {
                        const base = Number(pendingPrice);
                        if (discPct > 0) {
                          const discounted = (
                            base *
                            (1 - discPct / 100)
                          ).toFixed(2);
                          priceLabel = ` at $${discounted}/${cycleLabel}`;
                        } else {
                          priceLabel = ` at $${base.toFixed(2)}/${cycleLabel}`;
                        }
                      }
                      return (
                        <div className="subscription-pending-downgrade-notice">
                          <span className="pending-icon">⏳</span>
                          <div className="pending-details">
                            <strong>{t("ab.downgrade.scheduled")}</strong>
                            <span>
                              {t("ab.downgrade.switchNotice")
                                .replace("{name}", pendingName)
                                .replace("{price}", priceLabel)
                                .replace(
                                  "{date}",
                                  currentSubscription.pendingPlanChangeDate
                                    ? formatDate(
                                        currentSubscription.pendingPlanChangeDate,
                                      )
                                    : t("ab.downgrade.nextBillingDate"),
                                )}
                            </span>
                          </div>
                          <button
                            className="btn btn-ghost btn-sm"
                            onClick={() => handleCancelDowngrade()}
                            disabled={subscribing}
                          >
                            {subscribing
                              ? t("ab.downgrade.cancelling")
                              : t("ab.downgrade.keepCurrentPlan")}
                          </button>
                        </div>
                      );
                    })()}

                  {/* Scheduled Promo Notice — a promo code is queued for the
                      next billing date; current period is unaffected. */}
                  {currentSubscription.pendingPromoCode &&
                    !currentSubscription.cancelledAt &&
                    currentSubscription.status?.toLowerCase() !== "cancelled" &&
                    currentSubscription.status?.toLowerCase() !== "expired" && (
                      <div className="subscription-pending-promo-notice">
                        <div className="pending-details">
                          <div className="pending-title-row">
                            <span className="pending-icon">🏷️</span>
                            <strong>{t("ab.promo.scheduled.title")}</strong>
                          </div>
                          <span>
                            {t("ab.promo.scheduled.codePrefix")}{" "}
                            <code>{currentSubscription.pendingPromoCode}</code>
                            {currentSubscription.pendingDiscountPercent
                              ? ` ${t("ab.promo.scheduled.discountSuffix").replace("{pct}", Number(currentSubscription.pendingDiscountPercent))}`
                              : ""}{" "}
                            {t("ab.promo.scheduled.effectOn").replace(
                              "{date}",
                              currentSubscription.pendingPromoEffectiveDate
                                ? formatDate(
                                    currentSubscription.pendingPromoEffectiveDate,
                                  )
                                : t("ab.promo.scheduled.nextBillingDate"),
                            )}
                          </span>
                        </div>
                        <button
                          className="btn btn-ghost btn-sm"
                          onClick={() => handleCancelPendingPromo()}
                          disabled={subscribing}
                        >
                          {subscribing
                            ? t("ab.promo.scheduled.cancelling")
                            : t("ab.promo.scheduled.remove")}
                        </button>
                      </div>
                    )}

                  {/* Trial: a promo code saved on the trial auto-applies when it
                      converts to paid. Show what's locked in, with an option to
                      remove it. */}
                  {currentSubscription.isTrial &&
                    savedTrialPromoCode &&
                    !currentSubscription.cancelledAt &&
                    currentSubscription.status?.toLowerCase() === "active" && (
                      <div className="subscription-pending-promo-notice">
                        <div className="pending-details">
                          <div className="pending-title-row">
                            <span className="pending-icon">🏷️</span>
                            <strong>{t("ab.promo.trial.noticeTitle")}</strong>
                          </div>
                          <span>
                            {t("ab.promo.trial.noticeBody")
                              .replace("{code}", savedTrialPromoCode)
                              .replace(
                                "{discount}",
                                savedTrialPromoPct > 0
                                  ? ` (${savedTrialPromoPct}% off)`
                                  : "",
                              )}
                          </span>
                        </div>
                        <button
                          className="btn btn-ghost btn-sm"
                          onClick={() => handleCancelPendingPromo()}
                          disabled={subscribing}
                        >
                          {subscribing
                            ? t("ab.promo.scheduled.cancelling")
                            : t("ab.promo.trial.remove")}
                        </button>
                      </div>
                    )}

                  {/* Trial: lock in a promo code now so it auto-applies when the
                      trial converts to a paid plan. No payment method needed yet. */}
                  {currentSubscription.isTrial &&
                    !savedTrialPromoCode &&
                    !currentSubscription.cancelledAt &&
                    currentSubscription.status?.toLowerCase() === "active" && (
                      <div className="subscription-apply-promo">
                        {!showSubPromoInput ? (
                          <button
                            className="subscription-apply-promo__toggle"
                            type="button"
                            onClick={() => setShowSubPromoInput(true)}
                          >
                            🏷️ {t("ab.promo.trial.toggle")}
                          </button>
                        ) : (
                          <div className="subscription-apply-promo__panel subscription-apply-promo__panel--trial">
                            <label className="subscription-apply-promo__label">
                              {t("ab.promo.trial.label")}
                            </label>
                            <p className="subscription-apply-promo__hint">
                              {t("ab.promo.trial.hint")}
                            </p>
                            {!currentSubscription.paymentMethodOnFile && (
                              <div className="subscription-apply-promo__payment-notice">
                                <span>
                                  <span aria-hidden="true">⚠️</span>{" "}
                                  {t("ab.promo.trial.paymentNotice")}
                                </span>
                                <button
                                  className="subscription-apply-promo__payment-link"
                                  type="button"
                                  onClick={openUpdatePaymentMethod}
                                >
                                  {t("ab.action.addPaymentMethod")}
                                </button>
                              </div>
                            )}
                            <div className="subscription-apply-promo__row">
                              <input
                                type="text"
                                className="subscription-apply-promo__input"
                                value={subPromoCode}
                                placeholder={t("ab.promo.apply.placeholder")}
                                disabled={subPromoValidating || subscribing}
                                onChange={(e) => {
                                  setSubPromoCode(e.target.value.toUpperCase());
                                  if (subPromoResult) setSubPromoResult(null);
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    e.preventDefault();
                                    handleValidateSubPromoCode();
                                  }
                                }}
                              />
                            </div>
                            {subPromoResult && (
                              <div
                                className={`subscription-apply-promo__message ${
                                  subPromoResult.valid
                                    ? "is-valid"
                                    : "is-invalid"
                                }`}
                              >
                                {subPromoResult.valid ? (
                                  <>
                                    ✅ {subPromoResult.message}
                                    {subPromoResult.discountedPrice != null && (
                                      <span className="subscription-apply-promo__pricing">
                                        {" "}
                                        {t("ab.promo.trial.newPrice")
                                          .replace(
                                            "{price}",
                                            parseFloat(
                                              subPromoResult.discountedPrice,
                                            ).toFixed(2),
                                          )
                                          .replace(
                                            "{cycle}",
                                            (
                                              currentSubscription.pendingPaymentCycle ||
                                              currentSubscription.paymentCycle ||
                                              "mo"
                                            ).toLowerCase(),
                                          )}
                                      </span>
                                    )}
                                  </>
                                ) : (
                                  <>⚠️ {subPromoResult.message}</>
                                )}
                              </div>
                            )}
                            <div className="subscription-apply-promo__action-row">
                              {!subPromoResult?.valid ? (
                                <button
                                  className="btn btn-secondary btn-sm"
                                  type="button"
                                  onClick={handleValidateSubPromoCode}
                                  disabled={
                                    subPromoValidating || !subPromoCode.trim()
                                  }
                                >
                                  {subPromoValidating
                                    ? t("ab.promo.apply.checking")
                                    : t("ab.promo.apply.check")}
                                </button>
                              ) : (
                                <button
                                  className="btn btn-primary btn-sm"
                                  type="button"
                                  onClick={handleScheduleSubPromo}
                                  disabled={subscribing}
                                >
                                  {subscribing
                                    ? t("ab.promo.trial.applying")
                                    : t("ab.promo.trial.applyBtn")}
                                </button>
                              )}
                              <button
                                className="subscription-apply-promo__cancel"
                                type="button"
                                title={t("ab.promo.apply.cancelTitle")}
                                onClick={resetSubPromoState}
                              >
                                ✕
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                  {/* Apply Promo Code — eligible active paid subs without an
                      existing or already-scheduled discount. The discount takes
                      effect at the next billing date. */}
                  {!currentSubscription.pendingPromoCode &&
                    !currentSubscription.isTrial &&
                    currentSubscription.status?.toLowerCase() === "active" &&
                    currentSubscription.autoRenew &&
                    !currentSubscription.cancelledAt &&
                    !(currentSubscription.discountPercent > 0) &&
                    !currentSubscription.promoCode && (
                      <div className="subscription-apply-promo">
                        {!showSubPromoInput ? (
                          <button
                            className="subscription-apply-promo__toggle"
                            type="button"
                            onClick={() => setShowSubPromoInput(true)}
                          >
                            🏷️ {t("ab.promo.apply.toggle")}
                          </button>
                        ) : (
                          <div className="subscription-apply-promo__panel">
                            <label className="subscription-apply-promo__label">
                              {t("ab.promo.apply.label")}
                            </label>
                            <div className="subscription-apply-promo__row">
                              <input
                                type="text"
                                className="subscription-apply-promo__input"
                                value={subPromoCode}
                                placeholder={t("ab.promo.apply.placeholder")}
                                disabled={subPromoValidating || subscribing}
                                onChange={(e) => {
                                  setSubPromoCode(e.target.value.toUpperCase());
                                  if (subPromoResult) setSubPromoResult(null);
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    e.preventDefault();
                                    handleValidateSubPromoCode();
                                  }
                                }}
                              />
                            </div>
                            {subPromoResult && (
                              <div
                                className={`subscription-apply-promo__message ${
                                  subPromoResult.valid
                                    ? "is-valid"
                                    : "is-invalid"
                                }`}
                              >
                                {subPromoResult.valid ? (
                                  <>
                                    ✅ {subPromoResult.message}
                                    {subPromoResult.discountedPrice != null && (
                                      <span className="subscription-apply-promo__pricing">
                                        {" "}
                                        {t("ab.promo.apply.newPrice")
                                          .replace(
                                            "{price}",
                                            parseFloat(
                                              subPromoResult.discountedPrice,
                                            ).toFixed(2),
                                          )
                                          .replace(
                                            "{cycle}",
                                            (
                                              currentSubscription.pendingPaymentCycle ||
                                              currentSubscription.paymentCycle ||
                                              "mo"
                                            ).toLowerCase(),
                                          )}
                                      </span>
                                    )}
                                  </>
                                ) : (
                                  <>⚠️ {subPromoResult.message}</>
                                )}
                              </div>
                            )}
                            <div className="subscription-apply-promo__action-row">
                              {!subPromoResult?.valid ? (
                                <button
                                  className="btn btn-secondary btn-sm"
                                  type="button"
                                  onClick={handleValidateSubPromoCode}
                                  disabled={
                                    subPromoValidating || !subPromoCode.trim()
                                  }
                                >
                                  {subPromoValidating
                                    ? t("ab.promo.apply.checking")
                                    : t("ab.promo.apply.check")}
                                </button>
                              ) : (
                                <button
                                  className="btn btn-primary btn-sm"
                                  type="button"
                                  onClick={handleScheduleSubPromo}
                                  disabled={subscribing}
                                >
                                  {subscribing
                                    ? t("ab.promo.apply.applying")
                                    : t("ab.promo.apply.applyBtn")}
                                </button>
                              )}
                              <button
                                className="subscription-apply-promo__cancel"
                                type="button"
                                title={t("ab.promo.apply.cancelTitle")}
                                onClick={resetSubPromoState}
                              >
                                ✕
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                  {/* Plan Details — Expandable */}
                  <div className="subscription-details-section">
                    <button
                      className="subscription-details-toggle"
                      onClick={() => setShowPlanDetails(!showPlanDetails)}
                    >
                      <span>{t("ab.planDetails.toggle")}</span>
                      <FontAwesomeIcon
                        icon={faChevronRight}
                        className={`accordion-chevron ${showPlanDetails ? "expanded" : ""}`}
                      />
                    </button>

                    {showPlanDetails && (
                      <div className="subscription-features-panel">
                        <div className="subscription-features-grid-new">
                          <div className="features-column">
                            <h4>{t("ab.planDetails.limitsHeading")}</h4>
                            {renderFeature(
                              t("ab.planDetails.users"),
                              currentServicePlan?.maxUsers ||
                                currentSubscription.maxUsers ||
                                "1",
                            )}
                            {renderFeature(
                              t("ab.planDetails.simsPerMonth"),
                              currentServicePlan?.maxSimulationsPerMonth ||
                                currentSubscription.maxSimulationsPerMonth ||
                                "5",
                            )}
                            {renderFeature(
                              t("ab.planDetails.reportsPerMonth"),
                              currentServicePlan?.maxReportsPerMonth ||
                                currentSubscription.maxReportsPerMonth ||
                                "2",
                            )}
                          </div>
                          <div className="features-column">
                            <h4>{t("ab.planDetails.featuresHeading")}</h4>
                            {renderFeature(
                              t("ab.planDetails.scamDetection"),
                              true,
                              true,
                            )}
                            {renderFeature(
                              t("ab.planDetails.phishingSimulations"),
                              true,
                              true,
                            )}
                            {renderFeature(
                              t("ab.planDetails.scamAlerts"),
                              true,
                              true,
                            )}
                            {renderFeature(
                              t("ab.planDetails.advancedAnalytics"),
                              currentServicePlan?.hasAdvancedAnalytics ??
                                currentSubscription.hasAdvancedAnalytics,
                              true,
                            )}
                            {renderFeature(
                              t("ab.planDetails.customTraining"),
                              currentServicePlan?.hasCustomTraining ??
                                currentSubscription.hasCustomTraining,
                              true,
                            )}
                            {renderFeature(
                              t("ab.planDetails.prioritySupport"),
                              currentServicePlan?.hasPrioritySupport ??
                                currentSubscription.hasPrioritySupport,
                              true,
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Selected Plan Preview */}
                  {selectedPlan && (
                    <div ref={selectedPlanRef}>
                      {subscriptionError && (
                        <div
                          className="subscription-error-banner"
                          ref={errorRef}
                        >
                          <div className="error-banner-header">
                            <span className="error-icon">⚠️</span>
                            <strong>{subscriptionError.title}</strong>
                            {subscriptionError.type ===
                              "user_limit_exceeded" && (
                              <button
                                className="btn btn-secondary"
                                onClick={() => {
                                  setSubscriptionError(null);
                                  handleViewPlans();
                                }}
                              >
                                {t("ab.action.chooseDifferentPlan")}
                              </button>
                            )}
                            {subscriptionError.type === "payment_required" && (
                              <button
                                className="btn btn-secondary"
                                onClick={() => {
                                  setExpandedSection("payment");
                                  setTimeout(() => {
                                    const el =
                                      document.getElementById(
                                        "ab-section-payment",
                                      );
                                    if (el) {
                                      el.scrollIntoView({
                                        behavior: "smooth",
                                        block: "start",
                                      });
                                    }
                                  }, 150);
                                }}
                              >
                                {t("ab.action.addPaymentMethod")}
                              </button>
                            )}
                            <button
                              className="error-close-btn"
                              onClick={() => setSubscriptionError(null)}
                              aria-label={t("ab.action.closeError")}
                            >
                              ×
                            </button>
                          </div>
                          <p className="error-message">
                            {subscriptionError.message}
                          </p>
                          {subscriptionError.action && (
                            <p className="error-action">
                              {subscriptionError.action}
                            </p>
                          )}
                        </div>
                      )}

                      <SelectedPlanPreview
                        selectedPlan={selectedPlan}
                        currentSubscription={currentSubscription}
                        subscribing={subscribing}
                        handleSubscribeToPlan={handleSubscribeToPlan}
                        handleClearSelectedPlan={handleClearSelectedPlan}
                        handleViewPlans={handleViewPlans}
                      />
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="btn-group">
                    {/* Hide plan-change and cancellation controls for demo seats.
                        Demo seats run on the zero-dollar EVENT_DEMO_FULL_ACCESS plan
                        and have no Stripe customer ID — these actions would throw.
                        The readable plan name and features above remain visible. */}
                    {!isDemoSeat && (
                      <>
                        {/* Hide Change Plan when cancelled or cancellation is pending — user must resubscribe first */}
                        {!(
                          currentSubscription.status?.toLowerCase() ===
                            "cancelled" ||
                          (currentSubscription.status?.toLowerCase() ===
                            "active" &&
                            currentSubscription.cancelledAt)
                        ) && (
                          <button
                            className="btn btn-primary"
                            onClick={handleViewPlans}
                          >
                            {selectedPlan
                              ? t("ab.action.chooseDifferentPlan")
                              : t("ab.action.changePlan")}
                          </button>
                        )}
                        {/* Convert Trial to Full Subscription — only shown during active trial */}
                        {currentSubscription.isTrial &&
                          currentSubscription.status?.toLowerCase() ===
                            "active" &&
                          !currentSubscription.cancelledAt && (
                            <button
                              className="btn btn-secondary"
                              onClick={handleConvertTrialToPaid}
                              disabled={subscribing}
                            >
                              {subscribing
                                ? t("ab.action.converting")
                                : t("ab.action.convertToFull")}
                            </button>
                          )}
                        {currentSubscription.status?.toLowerCase() ===
                          "active" &&
                          !currentSubscription.cancelledAt && (
                            <button
                              className="btn btn-destructive"
                              onClick={() => handleCancelSubscription()}
                            >
                              {t("ab.action.cancelSubscription")}
                            </button>
                          )}
                        {currentSubscription.status?.toLowerCase() ===
                          "grace_period" && (
                          <button
                            className="btn btn-destructive"
                            onClick={() => handleCancelSubscription()}
                          >
                            {t("ab.action.cancelSubscription")}
                          </button>
                        )}
                        {/* Resubscribe button: shown when subscription is CANCELLED */}
                        {currentSubscription.status?.toLowerCase() ===
                          "cancelled" && (
                          <button
                            className="btn btn-secondary"
                            onClick={() => handleUndoCancelSubscription()}
                            disabled={subscribing}
                          >
                            {subscribing
                              ? t("ab.action.resubscribing")
                              : t("ab.action.resubscribe")}
                          </button>
                        )}
                        {/* Resubscribe button: shown when cancellation is pending (ACTIVE + cancelledAt) */}
                        {currentSubscription.status?.toLowerCase() ===
                          "active" &&
                          currentSubscription.cancelledAt && (
                            <button
                              className="btn btn-secondary"
                              onClick={() => handleUndoCancelSubscription()}
                              disabled={subscribing}
                            >
                              {subscribing
                                ? t("ab.action.resubscribing")
                                : t("ab.action.resubscribe")}
                            </button>
                          )}
                        {/* Show cancellation pending info if scheduled to cancel */}
                        {currentSubscription.status?.toLowerCase() ===
                          "active" &&
                          currentSubscription.cancelledAt && (
                            <span className="cancellation-pending-badge">
                              {t("ab.cancellationPending")}
                            </span>
                          )}
                      </>
                    )}
                  </div>
                </>
              ) : (
                /* No Subscription State */
                <div className="no-subscription-state">
                  <h2>{t("ab.noSubscription.title")}</h2>
                  <p>{t("ab.noSubscription.subtitle")}</p>

                  {subscriptionError && (
                    <div className="subscription-error-banner" ref={errorRef}>
                      <div className="error-banner-header">
                        <span className="error-icon">⚠️</span>
                        <strong>{subscriptionError.title}</strong>
                        {subscriptionError.type === "payment_required" && (
                          <button
                            className="btn btn-secondary"
                            onClick={() => {
                              setExpandedSection("payment");
                              setTimeout(() => {
                                const el =
                                  document.getElementById("ab-section-payment");
                                if (el) {
                                  el.scrollIntoView({
                                    behavior: "smooth",
                                    block: "start",
                                  });
                                }
                              }, 150);
                            }}
                          >
                            {t("ab.action.addPaymentMethod")}
                          </button>
                        )}
                        <button
                          className="error-close-btn"
                          onClick={() => setSubscriptionError(null)}
                          aria-label={t("ab.action.closeError")}
                        >
                          ×
                        </button>
                      </div>
                      <p className="error-message">
                        {subscriptionError.message}
                      </p>
                    </div>
                  )}

                  {selectedPlan ? (
                    <div ref={selectedPlanRef}>
                      <SelectedPlanPreview
                        selectedPlan={selectedPlan}
                        currentSubscription={currentSubscription}
                        subscribing={subscribing}
                        handleSubscribeToPlan={handleSubscribeToPlan}
                        handleClearSelectedPlan={handleClearSelectedPlan}
                        handleViewPlans={handleViewPlans}
                      />
                    </div>
                  ) : (
                    <button
                      className="btn btn-primary"
                      onClick={handleViewPlans}
                    >
                      {t("ab.action.browsePlans")}
                    </button>
                  )}
                </div>
              )}

              {/* Back to Setup Journey link — shown when navigated from AccountSetupJourney */}
              {initialSection === "subscription" && (
                <div className="setup-journey-return">
                  <button
                    className="setup-journey-return-link"
                    onClick={() => {
                      sessionStorage.setItem("expandSetupJourney", "true");
                      setHeroContent("Dashboard");
                    }}
                  >
                    <span className="return-arrow">←</span>
                    {t("ab.setupJourney.return")}
                  </button>
                </div>
              )}
            </div>
          ),
        })}

        {/* ═══════════════════════════════════════════════════ */}
        {/*  SECTION 3 — Payment Methods                      */}
        {/* ═══════════════════════════════════════════════════ */}
        {/* Hidden for demo seats: no Stripe customer, adding a card would fail. */}
        {!isDemoSeat &&
          renderAccordion({
            id: "payment",
            icon: faCreditCard,
            iconClass: "payment",
            title: t("ab.section.payment.title"),
            subtitle: t("ab.section.payment.subtitle"),
            expanded: expandedSection === "payment",
            onToggle: () => toggleSection("payment"),
            children: (
              <PaymentMethods
                autoOpenAddForm={autoOpenAddCard || forceOpenAddCard}
                onAddFormClosed={() => setForceOpenAddCard(false)}
                currentSubscription={currentSubscription}
                onCompleteSubscription={() => {
                  // Clear any stale error from a previous attempt
                  setSubscriptionError(null);

                  // Navigate to the subscription section
                  setExpandedSection("subscription");
                  setTimeout(() => {
                    const el = document.getElementById(
                      "ab-section-subscription",
                    );
                    if (el) {
                      el.scrollIntoView({ behavior: "smooth", block: "start" });
                    }
                  }, 150);

                  // Payment method is now on file — activate the saved plan.
                  if (selectedPlan) {
                    handleSubscribeToPlan(
                      currentSubscription,
                      false,
                      true,
                      null,
                      null,
                    );
                  }
                }}
                onBackToSetup={
                  initialSection === "payment"
                    ? () => {
                        sessionStorage.setItem("expandSetupJourney", "true");
                        setHeroContent("Dashboard");
                      }
                    : undefined
                }
              />
            ),
          })}

        {/* ═══════════════════════════════════════════════════ */}
        {/*  SECTION 4 — Billing History                      */}
        {/* ═══════════════════════════════════════════════════ */}
        {/* Hidden for demo seats: no Stripe subscription — history would be empty/broken. */}
        {!isDemoSeat &&
          renderAccordion({
            id: "history",
            icon: faReceipt,
            iconClass: "history",
            title: t("ab.section.history.title"),
            subtitle: t("ab.section.history.subtitle"),
            expanded: expandedSection === "history",
            onToggle: () => toggleSection("history"),
            children: <PaymentHistory />,
          })}

        {/* ═══════════════════════════════════════════════════ */}
        {/*  SECTION 5 — Subscription Change Log              */}
        {/*  NOTE: Reserved for Platform Admin only.           */}
        {/*  TODO: Move this panel to a future Platform Admin  */}
        {/*        menu instead of showing it to end users.    */}
        {/* ═══════════════════════════════════════════════════ */}
        {/* {!isDemoSeat &&
          renderAccordion({
            id: "auditlog",
            icon: faClipboardList,
            iconClass: "history",
            title: "Change Log",
            subtitle: "Review every subscription change on your account",
            expanded: expandedSection === "auditlog",
            onToggle: () => toggleSection("auditlog"),
            children: <SubscriptionAuditLog />,
          })} */}

        {/* ═══════════════════════════════════════════════════ */}
        {/*  MODALS                                            */}
        {/* ═══════════════════════════════════════════════════ */}
        {/* ServicePlans Modal */}
        {showServicePlans && (
          <ServicePlans
            isModal={true}
            onClose={handleCloseServicePlans}
            onPlanSelected={handlePlanSelectedFromModal}
          />
        )}
        {/* Plan Change Confirmation Modal */}
        <ConfirmationModal
          isOpen={showPlanChangeConfirm}
          onClose={cancelPlanChange}
          onConfirm={confirmPlanChange}
          title={
            planChangeDetails &&
            planChangeDetails.newPrice <
              (planChangeDetails.currentBasePrice ??
                planChangeDetails.currentPrice)
              ? t("ab.modal.planChange.titleDowngrade")
              : t("ab.modal.planChange.titleChange")
          }
          message={
            planChangeDetails &&
            planChangeDetails.newPrice <
              (planChangeDetails.currentBasePrice ??
                planChangeDetails.currentPrice)
              ? t("ab.modal.planChange.messageDowngrade")
              : t("ab.modal.planChange.messageChange")
          }
          details={
            planChangeDetails
              ? (() => {
                  const isDowngrade =
                    planChangeDetails.newPrice <
                    (planChangeDetails.currentBasePrice ??
                      planChangeDetails.currentPrice);
                  const isUpgrade =
                    planChangeDetails.newPrice >
                    (planChangeDetails.currentBasePrice ??
                      planChangeDetails.currentPrice);
                  const currentDiscountPct =
                    parseFloat(currentSubscription?.discountPercent) || 0;
                  const nextDate = currentSubscription
                    ? calculateNextPaymentDate(
                        currentSubscription.startDate,
                        currentSubscription.paymentCycle,
                        currentSubscription,
                      )
                    : null;
                  const formattedNextDate = nextDate
                    ? formatDate(nextDate)
                    : t("ab.renewal.date.endOfBillingPeriod");
                  const currentCycleLabel =
                    planChangeDetails.currentCycle?.toUpperCase() === "YEARLY"
                      ? "yr"
                      : "mo";

                  if (isDowngrade) {
                    return (
                      <div className="modal-plan-change-details">
                        <div className="modal-plan-comparison">
                          <div className="modal-plan-column current">
                            <span className="modal-plan-column-label">
                              {t("ab.modal.planChange.labelCurrent")}
                            </span>
                            <span className="modal-plan-column-name">
                              {planChangeDetails.currentPlan}
                            </span>
                            <span className="modal-plan-column-price">
                              {currentDiscountPct > 0 ? (
                                <>
                                  <span className="modal-plan-price-original">
                                    $
                                    {planChangeDetails.currentBasePrice ??
                                      planChangeDetails.currentPrice}
                                    /{currentCycleLabel}
                                  </span>
                                  <span className="modal-plan-price-discounted">
                                    ${planChangeDetails.currentPrice}/
                                    {currentCycleLabel}
                                  </span>
                                </>
                              ) : (
                                `$${planChangeDetails.currentPrice}/${currentCycleLabel}`
                              )}
                            </span>
                            <span className="modal-plan-column-users">
                              {t("ab.modal.planChange.users").replace(
                                "{n}",
                                planChangeDetails.currentUsers,
                              )}
                            </span>
                          </div>
                          <div className="modal-plan-arrow">→</div>
                          <div className="modal-plan-column new">
                            <span className="modal-plan-column-label">
                              {t("ab.modal.planChange.labelNew")}
                            </span>
                            <span className="modal-plan-column-name">
                              {planChangeDetails.newPlan}
                            </span>
                            <span className="modal-plan-column-price">
                              {planChangeDetails.promoCode &&
                              planChangeDetails.discountedPrice != null ? (
                                <>
                                  <span className="modal-plan-price-original">
                                    ${planChangeDetails.newPrice}/mo
                                  </span>
                                  <span className="modal-plan-price-discounted">
                                    $
                                    {planChangeDetails.discountedPrice.toFixed(
                                      2,
                                    )}
                                    /mo
                                  </span>
                                </>
                              ) : (
                                `$${planChangeDetails.newPrice}/mo`
                              )}
                            </span>
                            {planChangeDetails.promoCode && (
                              <span className="modal-plan-promo-badge">
                                🏷️ {planChangeDetails.promoCode}
                              </span>
                            )}
                            <span className="modal-plan-column-users">
                              {t("ab.modal.planChange.users").replace(
                                "{n}",
                                planChangeDetails.newUsers,
                              )}
                            </span>
                          </div>
                        </div>
                        <div className="modal-plan-notes">
                          <div className="modal-plan-note">
                            <span className="modal-plan-note-icon">📌</span>
                            <span>
                              {t("ab.modal.planChange.noteCurrent").replace(
                                "{date}",
                                formattedNextDate,
                              )}
                            </span>
                          </div>
                          <div className="modal-plan-note">
                            <span className="modal-plan-note-icon">🔄</span>
                            <span>{t("ab.modal.planChange.noteNewRate")}</span>
                          </div>
                          {currentDiscountPct > 0 &&
                            !planChangeDetails.promoCode && (
                              <div className="modal-plan-note warn">
                                <span className="modal-plan-note-icon">⚠️</span>
                                <span>
                                  {t(
                                    "ab.modal.planChange.noteDiscountWarning",
                                  ).replace("{pct}", currentDiscountPct)}
                                </span>
                              </div>
                            )}
                        </div>
                      </div>
                    );
                  }

                  // Upgrade or lateral change
                  return (
                    <div className="modal-plan-change-details">
                      <div className="modal-plan-comparison">
                        <div className="modal-plan-column current">
                          <span className="modal-plan-column-label">
                            {t("ab.modal.planChange.labelCurrent")}
                          </span>
                          <span className="modal-plan-column-name">
                            {planChangeDetails.currentPlan}
                          </span>
                          <span className="modal-plan-column-price">
                            {currentDiscountPct > 0 ? (
                              <>
                                <span className="modal-plan-price-original">
                                  $
                                  {planChangeDetails.currentBasePrice ??
                                    planChangeDetails.currentPrice}
                                  /{currentCycleLabel}
                                </span>
                                <span className="modal-plan-price-discounted">
                                  ${planChangeDetails.currentPrice}/
                                  {currentCycleLabel}
                                </span>
                              </>
                            ) : (
                              `$${planChangeDetails.currentPrice}/${currentCycleLabel}`
                            )}
                          </span>
                          <span className="modal-plan-column-users">
                            {t("ab.modal.planChange.users").replace(
                              "{n}",
                              planChangeDetails.currentUsers,
                            )}
                          </span>
                        </div>
                        <div className="modal-plan-arrow">→</div>
                        <div className="modal-plan-column new">
                          <span className="modal-plan-column-label">
                            {t("ab.modal.planChange.labelNew")}
                          </span>
                          <span className="modal-plan-column-name">
                            {planChangeDetails.newPlan}
                          </span>
                          <span className="modal-plan-column-price">
                            {planChangeDetails.promoCode &&
                            planChangeDetails.discountedPrice != null ? (
                              <>
                                <span className="modal-plan-price-original">
                                  ${planChangeDetails.newPrice}/mo
                                </span>
                                <span className="modal-plan-price-discounted">
                                  $
                                  {planChangeDetails.discountedPrice.toFixed(2)}
                                  /mo
                                </span>
                              </>
                            ) : (
                              `$${planChangeDetails.newPrice}/mo`
                            )}
                          </span>
                          {planChangeDetails.promoCode && (
                            <span className="modal-plan-promo-badge">
                              🏷️ {planChangeDetails.promoCode}
                            </span>
                          )}
                          <span className="modal-plan-column-users">
                            {t("ab.modal.planChange.users").replace(
                              "{n}",
                              planChangeDetails.newUsers,
                            )}
                          </span>
                        </div>
                      </div>
                      <div className="modal-plan-notes">
                        <div className="modal-plan-note">
                          <span className="modal-plan-note-icon">
                            {isUpgrade ? "⚡" : "🔄"}
                          </span>
                          <span>
                            {isUpgrade
                              ? t("ab.modal.planChange.noteUpgradeImmediate")
                              : t("ab.modal.planChange.noteChangeImmediate")}
                          </span>
                        </div>
                        {currentDiscountPct > 0 &&
                          isUpgrade &&
                          !planChangeDetails.promoCode && (
                            <div className="modal-plan-note warn">
                              <span className="modal-plan-note-icon">⚠️</span>
                              <span>
                                {t(
                                  "ab.modal.planChange.noteDiscountMayNotCarry",
                                ).replace("{pct}", currentDiscountPct)}
                              </span>
                            </div>
                          )}
                      </div>
                    </div>
                  );
                })()
              : []
          }
          confirmText={
            planChangeDetails &&
            planChangeDetails.newPrice < planChangeDetails.currentPrice
              ? t("ab.modal.planChange.confirmDowngrade")
              : t("ab.modal.planChange.confirmChange")
          }
          cancelText={t("ab.modal.planChange.cancelKeep")}
          type={
            planChangeDetails &&
            planChangeDetails.newPrice < planChangeDetails.currentPrice
              ? "warning"
              : "info"
          }
        />
        {/* Cancel Subscription Confirmation Modal */}
        <ConfirmationModal
          isOpen={showCancelConfirm}
          onClose={cancelSubscriptionConfirm}
          onConfirm={executeCancelSubscription}
          title={t("ab.modal.cancel.title")}
          message={
            currentSubscription?.isTrial
              ? t("ab.modal.cancel.messageTrial")
              : t("ab.modal.cancel.messageDefault")
          }
          details={
            currentSubscription
              ? (() => {
                  const isTrial = currentSubscription.isTrial;
                  const endDate = isTrial
                    ? currentSubscription.trialEndDate ||
                      currentSubscription.endDate
                    : calculateNextPaymentDate(
                        currentSubscription.startDate,
                        currentSubscription.paymentCycle,
                        currentSubscription,
                      );
                  const formattedEnd = endDate
                    ? formatDate(endDate)
                    : t("ab.modal.cancel.endFallback");
                  const planName =
                    formatPlanName(
                      currentServicePlan?.displayName ||
                        currentSubscription.planName,
                    ) || "your plan";

                  const lines = [];

                  if (isTrial) {
                    lines.push(
                      t("ab.modal.cancel.keepTrialFeatures")
                        .replace("{plan}", planName)
                        .replace("{date}", formattedEnd),
                      t("ab.modal.cancel.noTrialCharges"),
                    );
                  } else {
                    lines.push(
                      t("ab.modal.cancel.keepFeaturesUntil")
                        .replace("{plan}", planName)
                        .replace("{date}", formattedEnd),
                      t("ab.modal.cancel.willLoseAccess"),
                      t("ab.modal.cancel.noBillingAfter"),
                    );
                  }

                  lines.push(t("ab.modal.cancel.canResubscribe"));

                  return lines;
                })()
              : t("ab.modal.cancel.noSubText")
          }
          confirmText={t("ab.modal.cancel.confirmText")}
          cancelText={t("ab.modal.cancel.cancelText")}
          type="danger"
          showReasonInput={true}
          reasonPlaceholder={t("ab.modal.cancel.reasonPlaceholder")}
        />
        {/* Convert Trial to Full Subscription Confirmation Modal */}
        <ConfirmationModal
          isOpen={showConvertTrialConfirm}
          onClose={() => {
            cancelConvertTrialConfirm();
            resetConvertPromoState();
          }}
          onConfirm={() => {
            executeConvertTrialToPaid(
              currentSubscription,
              convertPromoResult?.valid
                ? convertPromoCode.trim().toUpperCase()
                : null,
            );
            resetConvertPromoState();
          }}
          title={t("ab.modal.convert.title")}
          message={t("ab.modal.convert.message")}
          details={
            <div>
              <ul style={{ margin: "0 0 12px 0", paddingLeft: "20px" }}>
                <li>
                  {t("ab.modal.convert.keepFeatures").replace(
                    "{plan}",
                    formatPlanName(
                      currentServicePlan?.displayName ||
                        currentSubscription?.planName,
                    ) || "plan",
                  )}
                </li>
                <li>
                  {t("ab.modal.convert.chargeInfo")
                    .replace(
                      "${amount}",
                      Number(
                        convertPromoResult?.valid
                          ? convertPromoResult.discountedPrice
                          : savedTrialPromoPct > 0
                            ? Number(
                                priceInfo?.displayPrice ??
                                  currentSubscription?.amount ??
                                  0,
                              ) *
                              (1 - savedTrialPromoPct / 100)
                            : (priceInfo?.displayPrice ??
                              currentSubscription?.amount ??
                              0),
                      ).toFixed(2),
                    )
                    .replace(
                      "{period}",
                      (
                        priceInfo?.period ||
                        currentSubscription?.paymentCycle ||
                        "Monthly"
                      ).toLowerCase(),
                    )}
                </li>
                <li>
                  {t(
                    currentSubscription?.autoRenew
                      ? "ab.modal.convert.autoRenewEnabled"
                      : "ab.modal.convert.autoRenewDisabled",
                  )}
                </li>
                <li>{t("ab.modal.convert.cancelAnytime")}</li>
              </ul>
              <div className="selected-plan-preview__promo-section">
                {savedTrialPromoCode && !convertPromoResult?.valid ? (
                  <div className="selected-plan-preview__promo-applied">
                    <div className="selected-plan-preview__promo-applied-badge">
                      <span className="selected-plan-preview__promo-check">
                        ✅
                      </span>
                      <span className="selected-plan-preview__promo-code-text">
                        {savedTrialPromoCode}
                        {savedTrialPromoPct > 0
                          ? ` (${savedTrialPromoPct}% off)`
                          : ""}
                      </span>
                    </div>
                    <div className="selected-plan-preview__promo-discount-summary">
                      <span className="selected-plan-preview__promo-message-success">
                        {t("ab.promo.trial.savedNote").replace(
                          "{code}",
                          savedTrialPromoCode,
                        )}
                      </span>
                    </div>
                  </div>
                ) : !showConvertPromoInput && !convertPromoResult?.valid ? (
                  <button
                    className="selected-plan-preview__promo-toggle"
                    onClick={() => setShowConvertPromoInput(true)}
                    type="button"
                  >
                    🏷️ {t("selectedPlan.promo.toggle")}
                  </button>
                ) : (
                  <div className="selected-plan-preview__promo-input-group">
                    <label className="selected-plan-preview__promo-label">
                      {t("selectedPlan.promo.label")}
                    </label>
                    {convertPromoResult?.valid ? (
                      <div className="selected-plan-preview__promo-applied">
                        <div className="selected-plan-preview__promo-applied-badge">
                          <span className="selected-plan-preview__promo-check">
                            ✅
                          </span>
                          <span className="selected-plan-preview__promo-code-text">
                            {convertPromoCode.toUpperCase()}
                          </span>
                          <button
                            className="selected-plan-preview__promo-remove"
                            onClick={() => {
                              setConvertPromoCode("");
                              setConvertPromoResult(null);
                              setShowConvertPromoInput(false);
                            }}
                            type="button"
                            title={t("selectedPlan.promo.remove")}
                          >
                            ✕
                          </button>
                        </div>
                        <div className="selected-plan-preview__promo-discount-summary">
                          <span className="selected-plan-preview__promo-message-success">
                            {convertPromoResult.message}
                          </span>
                          <div className="selected-plan-preview__promo-pricing">
                            <span className="selected-plan-preview__promo-original-price">
                              $
                              {parseFloat(
                                convertPromoResult.originalPrice,
                              ).toFixed(2)}
                              /
                              {(
                                priceInfo?.period ||
                                currentSubscription?.paymentCycle ||
                                "mo"
                              ).toLowerCase()}
                            </span>
                            <span className="selected-plan-preview__promo-arrow">
                              →
                            </span>
                            <span className="selected-plan-preview__promo-new-price">
                              $
                              {parseFloat(
                                convertPromoResult.discountedPrice,
                              ).toFixed(2)}
                              /
                              {(
                                priceInfo?.period ||
                                currentSubscription?.paymentCycle ||
                                "mo"
                              ).toLowerCase()}
                            </span>
                            <span className="selected-plan-preview__promo-savings">
                              (Save $
                              {parseFloat(
                                convertPromoResult.savingsAmount,
                              ).toFixed(2)}
                              /
                              {(
                                priceInfo?.period ||
                                currentSubscription?.paymentCycle ||
                                "mo"
                              ).toLowerCase()}
                              )
                            </span>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="selected-plan-preview__promo-form">
                        <div className="selected-plan-preview__promo-input-row">
                          <input
                            type="text"
                            value={convertPromoCode}
                            onChange={(e) => {
                              setConvertPromoCode(e.target.value.toUpperCase());
                              if (convertPromoResult)
                                setConvertPromoResult(null);
                            }}
                            placeholder={t("selectedPlan.promo.placeholder")}
                            className="selected-plan-preview__promo-input"
                            disabled={convertPromoValidating}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                handleValidateConvertPromoCode();
                              }
                            }}
                          />
                          <button
                            className="btn btn-primary selected-plan-preview__promo-apply-btn"
                            onClick={handleValidateConvertPromoCode}
                            disabled={
                              convertPromoValidating || !convertPromoCode.trim()
                            }
                            type="button"
                          >
                            {convertPromoValidating
                              ? t("selectedPlan.promo.checking")
                              : t("selectedPlan.promo.apply")}
                          </button>
                          <button
                            className="selected-plan-preview__promo-cancel-btn"
                            onClick={() => {
                              setShowConvertPromoInput(false);
                              setConvertPromoCode("");
                              setConvertPromoResult(null);
                            }}
                            type="button"
                            title={t("ab.promo.apply.cancelTitle")}
                          >
                            ✕
                          </button>
                        </div>
                        {convertPromoResult && !convertPromoResult.valid && (
                          <div className="selected-plan-preview__promo-error">
                            ❌ {convertPromoResult.message}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          }
          confirmText={t("ab.modal.convert.confirmText")}
          cancelText={t("ab.modal.convert.cancelText")}
          type="info"
        />
      </div>
    </div>
  );
}

export default SubscriptionPayment;
